import { Component, OnInit, OnDestroy } from '@angular/core';
import { AngularFirestore } from "@angular/fire/compat/firestore";
import * as XLSX from "xlsx";
import { Observable, Subscription } from "rxjs";
import { FullChild } from "../../../../interfaces/FullChild";
import { Centre } from "../../../../interfaces/Centres";
import { CentreService } from "../../../../services/centre.service";
import {PermissionType} from "../../../../components/employee/PermissionType.enum";
import {FinancialYearService} from "../../../../services/financial-year.service";

@Component({
  selector: 'app-hospital-count-report',
  templateUrl: './hospital-count-report.component.html',
  styleUrls: ['./hospital-count-report.component.scss']
})
export class HospitalCountReportComponent implements OnInit, OnDestroy {
  private searchSubscription: Subscription | null = null;
  mData: any[] = []
  tableData: any[] = []
  uniqueCentres: string[] = []
  uniqueAgeGroup: string[] = []
  startDate: Date | undefined | null
  endDate: Date | undefined | null
  uniqueAgeData: any[] = []
  followUpAgeData: any[] = []
  fileName = 'Hospital_Count_Report.xlsx';
  selectedCentreCode: any = 'All'
  isLoading = false;  // New loading flag
  isButtonDisabled: boolean = false;
  centres$: Observable<Centre[]>;
  selectedCentre:any;
  displayCentre = (c: any) => c?.centreName;
  mSnackbar: any;

  constructor(
    private mFirestore: AngularFirestore,
    private mCentreService: CentreService,
    private readonly financialYearService: FinancialYearService
  ) {
    this.centres$ = this.mCentreService.getAllCentres();
  }

  ngOnInit(): void {
    const fy = this.financialYearService.getCurrentFinancialYear();
    this.startDate = fy.startDate;
    this.endDate = fy.endDate;
  }
  onCentreSelect(selectedCentre: Centre) {
    if (!selectedCentre) return;

    console.log("Selected Centre:", selectedCentre);

    this.selectedCentre = selectedCentre;

    // // 1️⃣ Set centre details in form
    // this.studentForm.patchValue({
    //   centreName: selectedCentre.centreName,
    //   centreCode: selectedCentre.centreCode
    // });
  }

  clearAll() {
    const fy = this.financialYearService.getCurrentFinancialYear();
    this.startDate = fy.startDate;
    this.endDate = fy.endDate;
    this.mData = [];
    this.tableData = [];
    this.uniqueCentres = [];
    this.selectedCentre=[];
    this.uniqueAgeGroup = [];
    this.uniqueAgeData = [];
    this.followUpAgeData = [];
    this.selectedCentreCode = ''
  }

  search() {
    // Unsubscribe from any previous search to prevent data accumulation
    if (this.searchSubscription) {
      this.searchSubscription.unsubscribe();
    }

    // Clear previous search results
    this.clearPreviousSearch();
    this.isLoading = true;  // Start loading
    this.isButtonDisabled = true;
    // Clear previous data to avoid accumulation of results
    this.mData = []; // Clear previous data

    if (this.startDate != null && this.endDate != null) {

      this.searchSubscription = this.mFirestore.collection<FullChild>('ALChild', ref => ref.orderBy('centreName'))
        .get()
        .subscribe(res => {
          let childbyadmission: FullChild[] = [];
  let d=   res.docs.map(x=>x.data())
          // Process admission data
          d.forEach(it => {
            it.admissionData.forEach((c, i) => {
              if (i % 2 == 0) { // Handles alternating admission/follow-up entries
                if (c.dateOfAdmission?.toDate()!! >= this.startDate!! && c.dateOfAdmission?.toDate()!! <= this.endDate!!) {
                  childbyadmission.push(it); // Push child within date range
                }
              }
            });
          });

          // If "All Centre" is selected, include all centres
          if (this.selectedCentreCode !== "All") {
            childbyadmission = childbyadmission.filter(child => child.centreCode === this.selectedCentreCode);
          }

          let data2: any[] = [];
          let uniqueHospitals: Set<string> = new Set();

          // Store data with hospital, centre, and type ('Follow Up' or 'Unique')
          childbyadmission.forEach(child => {
            let type = child?.admissionData.length > 2 ? 'Follow Up' : 'Unique';

            let obj = {
              hospital: child.hospital,
              centre: child.centreName,
              type: type,
              childCode: child.childCode
            };
            this.mData.push(obj);
            uniqueHospitals.add(child.hospital);
          });

          // Sort hospitals alphabetically
          const sortedHospitals = Array.from(uniqueHospitals).sort();
          this.uniqueCentres = [...new Set(this.mData.map(child => child.centre))];

          // Initialize centre and hospital totals
          let centreTotals: { [key: string]: number } = {};
          let hospitalTotals: { [key: string]: number } = {};
          let totalRow: any[] = []; // Stores total for each row (i.e., hospital)
          let totalColumn: any = {}; // Stores total for each column (i.e., centre)

          // Initialize total column values
          this.uniqueCentres.forEach(centre => {
            totalColumn[centre] = 0;
          });

          let grandTotal = 0; // Grand total (for both rows and columns)

          // Process data to build the table
          sortedHospitals.forEach(hospital => {
            let hospitalData: any[] = [];
            let rowTotal = 0; // Total for this hospital (row)

            this.uniqueCentres.forEach(centre => {
              let count = this.mData.filter((child) => child.hospital === hospital && child.centre === centre).length;
              rowTotal += count; // Add to row total for hospital
              totalColumn[centre] += count; // Add to the column total for centre
              hospitalData.push({
                hospital: hospital,
                centre: centre,
                count: count
              });
            });

            // Push the row total to hospitalTotals
            hospitalTotals[hospital] = rowTotal;
            totalRow.push({ hospital: hospital, centre: 'Total', count: rowTotal }); // Row total for this hospital
            grandTotal += rowTotal; // Add row total to grand total

            data2 = data2.concat(hospitalData);
          });

          // Add row totals (column 'Total') for each hospital
          totalRow.forEach(row => {
            data2.push(row); // Add hospital total to the data
          });

          // Add column totals (last row: 'Total' row for each centre)
          this.uniqueCentres.forEach(centre => {
            data2.push({
              hospital: 'Total',
              centre: 'Total',
              count: totalColumn[centre] // Column total for this centre
            });
          });

          // Add final grand total at bottom-right (last cell with "Total" in both hospital and centre fields)
          data2.push({ hospital: 'Total', centre: 'Total', count: grandTotal });

          // Process tableData to display
          this.tableData = data2.reduce((acc, curr) => {
            const hospital = acc.find((item: any) => item.hospital === curr.hospital);
            if (hospital) {
              hospital.centres.push({ centre: curr.centre, count: curr.count });
            } else {
              acc.push({
                hospital: curr.hospital,
                centres: [{ centre: curr.centre, count: curr.count }]
              });
            }
            return acc;
          }, []);

          // Sort tableData by hospital name
          this.tableData = this.tableData.sort((a, b) => a.hospital.localeCompare(b.hospital));

          this.isLoading = false;  // Stop loading
          this.isButtonDisabled = false;
        });
    } else {
      this.mSnackbar.open('Please select a proper date range first', '', {
        panelClass: 'error',
        duration: 3000
      });
    }
  }


  ngOnDestroy(): void {
    if (this.searchSubscription) {
      this.searchSubscription.unsubscribe();
    }
  }

  clearPreviousSearch() {
    this.mData = [];
  }

  exportexcel() {
    let element = document.getElementById('excel-table');
    const ws: XLSX.WorkSheet = XLSX.utils.table_to_sheet(element);
    ws['!cols'] = [];
    ws['!cols'][19] = { hidden: true };

    const wb: XLSX.WorkBook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');
    XLSX.writeFile(wb, this.fileName);
  }
    onEndDateChanged() {
        this.endDate?.setHours(23, 59, 0, 0);
    }

  protected readonly PermissionType = PermissionType;
}
